export interface Experiment<ParamValues> {
    name: string;
    paramName: string;
    values: ParamValues;
}

export type ExperimentMap = Record<string, ExperimentData>;
export type ExperimentData = Record<string, unknown>;

export enum IncreaseAdsInHomepageExperimentValues {
    control = 'control',
    moreAds = 'more-ads',
}

export const IncreaseAdsInHomepageExperiment: Experiment<
    typeof IncreaseAdsInHomepageExperimentValues
> = {
    name: 'increase-ads-in-homepage',
    paramName: 'ad-quantity',
    values: IncreaseAdsInHomepageExperimentValues,
};

export enum SearchThirdAdValues {
    control = 'control',
    increaseAds = 'variant',
}

export const SearchThirdAd: Experiment<typeof SearchThirdAdValues> = {
    name: 'web_search_third_ad',
    paramName: 'variant',
    values: SearchThirdAdValues,
};

export enum SearchEventCardSignalsVisibilityExperimentValues {
    control = 'control',
    reduced = 'reduced',
}

export const SearchEventCardSignalsVisibilityExperiment: Experiment<
    typeof SearchEventCardSignalsVisibilityExperimentValues
> = {
    name: 'search-event-card-signals-visibility',
    paramName: 'signals_visibility',
    values: SearchEventCardSignalsVisibilityExperimentValues,
};

export enum SearchEventCardClickBehaviourExperimentValues {
    control = 'control',
    fullClick = 'full_click',
    // "Call to action"
    cta = 'cta',
}

export const SearchEventCardClickBehaviourExperiment: Experiment<
    typeof SearchEventCardClickBehaviourExperimentValues
> = {
    name: 'search_event_card_click_behavior',
    paramName: 'click_behavior',
    values: SearchEventCardClickBehaviourExperimentValues,
};

export const HomepageSeasonalCarouselValues = {
    control: 'hide',
    test: 'show',
};

export const HomepageSeasonalCarouselExperiment: Experiment<
    typeof HomepageSeasonalCarouselValues
> = {
    name: 'mardi_gras_new_orleans',
    paramName: 'mardi_gras_carousel',
    values: HomepageSeasonalCarouselValues,
};

export const DatingInNightlifeValues = {
    control: 'hide',
    test: 'show',
};

export const DatingInNightlifeExperiment: Experiment<
    typeof DatingInNightlifeValues
> = {
    name: 'dating_carousel_on_homepage',
    paramName: 'dating_tab',
    values: DatingInNightlifeValues,
};

export const DatingInHomepageValues = {
    control: 'hide',
    test: 'show',
};

export const DatingInHomepageExperiment: Experiment<
    typeof DatingInHomepageValues
> = {
    name: 'dating_carousel_on_homepage_v2',
    paramName: 'dating_carousel',
    values: DatingInHomepageValues,
};

export const EventsNearYouCarouselValues = {
    control: 'hide',
    test: 'show',
};

export const EventsNearYouCarouselExperiment: Experiment<
    typeof EventsNearYouCarouselValues
> = {
    name: 'homepage_events_near_you_carousel',
    paramName: 'events_near_you_carousel',
    values: EventsNearYouCarouselValues,
};

export const TrendingTagEventsValues = {
    control: 'hide',
    test: 'show',
};

export const TrendingTagEventsExperiment: Experiment<
    typeof TrendingTagEventsValues
> = {
    name: 'trending_tag_events_v2',
    paramName: 'trending_events',
    values: DatingInHomepageValues,
};

export const SearchDesktopMapValues = {
    control: 'show',
    variant: 'hide',
};

export const SearchDesktopMapExperiment: Experiment<
    typeof SearchDesktopMapValues
> = {
    name: 'removing_map_experiment2',
    paramName: 'map',
    values: SearchDesktopMapValues,
};

export enum DisplayAdFromRecentViewsExperimentValues {
    control = 'control',
    recentViews = 'user_interactions_viewed_inperson',
}

export const DisplayAdFromRecentViewsExperiment: Experiment<
    typeof DisplayAdFromRecentViewsExperimentValues
> = {
    name: 'homepage-for-you-display-ad-from-recent-views',
    paramName: 'bucket-name',
    values: DisplayAdFromRecentViewsExperimentValues,
};

export enum ExperienceFiltersExperimentValues {
    control = 'hide',
    test = 'show',
}

export const ExperienceFiltersExperiment: Experiment<
    typeof ExperienceFiltersExperimentValues
> = {
    name: 'search_experience_filters',
    paramName: 'show_experience_filters',
    values: ExperienceFiltersExperimentValues,
};

export enum DisplayAdFromUserLikesExperimentValues {
    control = 'control',
    recentLikes = 'user_interactions_liked_inperson',
}

export const DisplayAdFromUserLikesExperiment: Experiment<
    typeof DisplayAdFromUserLikesExperimentValues
> = {
    name: 'homepage_for_you_user_likes_show_ads',
    paramName: 'bucket-name',
    values: DisplayAdFromUserLikesExperimentValues,
};

export enum DisplayAdFromUserInterestsExperimentValues {
    control = 'control',
    userInterests = 'user_interests_inperson_events',
}

export const DisplayAdFromUserInterestsExperiment: Experiment<
    typeof DisplayAdFromUserInterestsExperimentValues
> = {
    name: 'homepage_for_you_user_interests_show_ads',
    paramName: 'bucket-name',
    values: DisplayAdFromUserInterestsExperimentValues,
};

export enum DisplayAdFromUserOrdersExperimentValues {
    control = 'control',
    userOrders = 'user_interactions_orders_inperson',
}

export const DisplayAdFromUserOrdersExperiment: Experiment<
    typeof DisplayAdFromUserOrdersExperimentValues
> = {
    name: 'homepage_for_you_user_orders_show_ads',
    paramName: 'bucket-name',
    values: DisplayAdFromUserOrdersExperimentValues,
};

export const TrendingSearchValues = {
    control: 'hide',
    test: 'show',
};

export const HomepageTrendingSearchExperiment: Experiment<
    typeof TrendingSearchValues
> = {
    name: 'homepage_trending_search',
    paramName: 'show_trending_search',
    values: TrendingSearchValues,
};

export const CategoryBrowseNightlifeExperiment: Experiment<
    typeof TrendingSearchValues
> = {
    name: 'category_browse_nightlife_events',
    paramName: 'show_nightlife_events',
    values: TrendingSearchValues,
};

export enum EventCardImageExperimentValues {
    control = 'control',
    multi = 'carousel_ads',
}

export const EventCardHomepageImageExperiment: Experiment<
    typeof EventCardImageExperimentValues
> = {
    name: 'homepage_carousel_image_ads',
    paramName: 'carousel_ads',
    values: EventCardImageExperimentValues,
};

export const BudgetEventsExperiment: Experiment<typeof TrendingSearchValues> = {
    name: 'budget_events_carousel_mobile',
    paramName: 'show_budget_events',
    values: TrendingSearchValues,
};
